export function sub<B extends string, const R extends Record<string, string>>(base: B, routes: R) {
  const result = { root: base } as { root: B } & { [K in keyof R]: `${B}/${R[K]}` };
  for (const key in routes) {
    (result as Record<string, string>)[key] = `${base}/${routes[key]}`;
  }
  return result;
}

export function createPathHelper<R extends string>(root: R) {
  return function pathHelper<T extends string>(segment: T): `/${R}/${T}` {
    return `/${root}/${segment}` as `/${R}/${T}`;
  };
}

type PathParamKeys<Path extends string> = Path extends `${string}:${infer Param}/${infer Rest}`
  ? Param | PathParamKeys<`/${Rest}`>
  : Path extends `${string}:${infer Param}`
    ? Param
    : never;

type PathParams<Path extends string> = [PathParamKeys<Path>] extends [never]
  ? Record<string, never>
  : { [K in PathParamKeys<Path>]: string };

export function interpolatePath<Path extends string>(
  pattern: Path,
  params: PathParams<Path>
): Path {
  let result = pattern as string;

  for (const [key, value] of Object.entries(params as Record<string, string>)) {
    result = result.replace(`:${key}`, value);
  }

  return result as Path;
}

export function createPathHelpers<const T extends Record<string, string>>(roots: T) {
  return Object.fromEntries(
    Object.entries(roots).map(([key, value]) => [key, createPathHelper(value)])
  ) as { [K in keyof T]: ReturnType<typeof createPathHelper<T[K]>> };
}
